import { type AcousticsResult, MAX_EARLY, type PathInfo, type RoomAcoustics, type SourceAcoustics } from '../acoustics/compute'
import type { Listener, Source } from '../model/schema'
import { Rng } from './dsp/generators'
import { downmixToMono, makeSeamlessLoop } from './dsp/loop'
import workletUrl from './worklet.ts?worker&url'

export type Layer = 'direct' | 'early' | 'late'

/** Reference loudness of a source at 1 m (generators are normalised to ~-20 dBFS RMS). */
const SOURCE_LEVEL = 4
/** Longest propagation delay a path can have (~2.4 km). Longer paths are far beyond audibility and are muted. */
const MAX_DELAY = 7
const GAIN_TAU = 0.03
const MOVE_TAU = 0.04
const LISTENER_TAU = 0.012

export interface EngineOptions {
  /** Apply parameter changes instantly instead of smoothing (offline rendering). */
  immediate?: boolean
  /** Room used to configure the reverb tail from the first sample (otherwise a default until the first update). */
  room?: { delays: number[]; rt60Low: number; rt60High: number }
}

interface Clip {
  mono: Float32Array
  sampleRate: number
}

type Param = AudioParam

function pannerFor(ctx: BaseAudioContext): PannerNode {
  return new PannerNode(ctx, {
    panningModel: 'HRTF',
    distanceModel: 'inverse',
    refDistance: 1,
    maxDistance: 10000,
    // Distance loss is applied explicitly per path; the panner supplies direction only.
    rolloffFactor: 0,
    coneInnerAngle: 360,
  })
}

/** One propagation path: delay → gain → lowpass → (HRTF panner). */
class Chain {
  readonly input: DelayNode
  private gain: GainNode
  private lp: BiquadFilterNode
  private panner: PannerNode | null
  private seen = false

  constructor(
    private eng: AudioEngine,
    destination: AudioNode,
    spatial: boolean,
  ) {
    const ctx = eng.ctx
    this.input = new DelayNode(ctx, { maxDelayTime: MAX_DELAY, delayTime: 0 })
    this.gain = new GainNode(ctx, { gain: 0 })
    this.lp = new BiquadFilterNode(ctx, { type: 'lowpass', frequency: 20000, Q: Math.SQRT1_2 })
    this.input.connect(this.gain)
    this.gain.connect(this.lp)
    if (spatial) {
      this.panner = pannerFor(ctx)
      this.lp.connect(this.panner)
      this.panner.connect(destination)
    } else {
      this.panner = null
      this.lp.connect(destination)
    }
  }

  /** Forget the previous path so the next one lands on its delay, filter and position instantly (the gain must be ~0). */
  reset() {
    this.seen = false
  }

  apply(path: Pick<PathInfo, 'valid' | 'gain' | 'delay' | 'cutoff' | 'apparent'>) {
    const e = this.eng
    const first = !this.seen
    const reachable = path.valid && path.delay < MAX_DELAY - 0.01
    if (reachable) {
      const delay = path.delay
      if (first) {
        this.input.delayTime.value = delay
        this.lp.frequency.value = path.cutoff
        if (this.panner) setPannerPosition(this.panner, path.apparent)
      } else {
        e.set(this.input.delayTime, delay, MOVE_TAU)
        e.set(this.lp.frequency, path.cutoff, GAIN_TAU)
        if (this.panner) e.setPannerPos(this.panner, path.apparent)
      }
      this.seen = true
    }
    e.set(this.gain.gain, reachable ? path.gain : 0, GAIN_TAU)
  }

  dispose() {
    this.input.disconnect()
    this.gain.disconnect()
    this.lp.disconnect()
    this.panner?.disconnect()
  }
}

function setPannerPosition(p: PannerNode, pos: [number, number, number]) {
  if (p.positionX) {
    p.positionX.value = pos[0]
    p.positionY.value = pos[1]
    p.positionZ.value = pos[2]
  } else {
    ;(p as unknown as { setPosition(x: number, y: number, z: number): void }).setPosition(pos[0], pos[1], pos[2])
  }
}

interface ClipPlayback {
  node: AudioBufferSourceNode
}

class Voice {
  /** Source signal ready for spatialisation: level + highfrequency cut applied. */
  readonly tap: GainNode
  private level: GainNode
  private hfCut: BiquadFilterNode
  /** Mute-bypassing entry for the click test. */
  private clickIn: GainNode
  readonly direct: Chain
  readonly early: Chain[] = []
  /** Which reflecting surface each early chain currently carries (stable ids), and when an emptied chain is safe to reuse. */
  private slotIds: (string | null)[] = []
  private slotFreeAt: number[] = []
  private send: Chain
  private clipFilterHp: BiquadFilterNode
  private clipFilterLp: BiquadFilterNode
  private clipIn: GainNode
  private genNode: AudioWorkletNode | null = null
  private genKey = ''
  private clipPlayers = new Set<ClipPlayback>()
  private retriggerTimer: ReturnType<typeof setTimeout> | null = null
  private stopTimer: ReturnType<typeof setTimeout> | null = null
  private rng = new Rng(1)
  private active = false
  private disposed = false
  private lastSource: Source
  private appliedPlay = false
  private clipKey = ''

  constructor(
    private eng: AudioEngine,
    source: Source,
  ) {
    const ctx = eng.ctx
    this.lastSource = source
    this.level = new GainNode(ctx, { gain: 0 })
    this.hfCut = new BiquadFilterNode(ctx, { type: 'lowpass', frequency: source.hfCut, Q: Math.SQRT1_2 })
    this.tap = new GainNode(ctx, { gain: 1 })
    this.clickIn = new GainNode(ctx, { gain: 1 })
    this.clipIn = new GainNode(ctx, { gain: 1 })
    this.clipFilterHp = new BiquadFilterNode(ctx, { type: 'highpass', frequency: 20, Q: Math.SQRT1_2 })
    this.clipFilterLp = new BiquadFilterNode(ctx, { type: 'lowpass', frequency: 20000, Q: Math.SQRT1_2 })
    this.clipIn.connect(this.clipFilterHp)
    this.clipFilterHp.connect(this.clipFilterLp)
    this.clipFilterLp.connect(this.level)
    this.level.connect(this.hfCut)
    this.hfCut.connect(this.tap)
    this.clickIn.connect(this.tap)

    this.direct = new Chain(eng, eng.layers.direct, true)
    this.tap.connect(this.direct.input)
    for (let i = 0; i < MAX_EARLY; i++) {
      const c = new Chain(eng, eng.layers.early, true)
      this.tap.connect(c.input)
      this.early.push(c)
      this.slotIds.push(null)
      this.slotFreeAt.push(0)
    }
    this.send = new Chain(eng, eng.reverbBus, false)
    this.tap.connect(this.send.input)
    this.update(source, false)
  }

  /** Reconcile with the latest scene description. `anySolo` is true when any source is soloed. */
  update(s: Source, anySolo: boolean) {
    if (this.disposed) return
    const e = this.eng
    const audible = s.playing && !s.mute && (!anySolo || s.solo)
    e.set(this.level.gain, audible ? s.gain * SOURCE_LEVEL : 0, GAIN_TAU)
    e.set(this.hfCut.frequency, s.hfCut, GAIN_TAU)
    // The click test bypasses mute but follows the source's level, so a +30 dB machine clicks across the hall too.
    e.set(this.clickIn.gain, Math.max(1, s.gain), GAIN_TAU)

    if (s.kind === 'generator') {
      this.ensureGenerator(s)
      if (this.genNode) {
        const p = this.genNode.parameters
        e.set(p.get('a')!, s.params[0], 0.05)
        e.set(p.get('b')!, s.params[1], 0.05)
        e.set(p.get('c')!, s.params[2], 0.05)
      }
      if (s.playing !== this.appliedPlay) this.setGeneratorRunning(s.playing)
    } else {
      this.dropGenerator()
      this.updateClip(s)
    }
    this.appliedPlay = s.playing
    this.lastSource = s
  }

  /** Start immediately; stop only after the level ramp has faded the output, so stopping never clicks. */
  private setGeneratorRunning(run: boolean) {
    if (this.stopTimer) clearTimeout(this.stopTimer)
    this.stopTimer = null
    if (run) this.genNode?.port.postMessage({ type: 'run', value: true })
    else this.stopTimer = setTimeout(() => this.genNode?.port.postMessage({ type: 'run', value: false }), this.eng.immediate ? 0 : 250)
  }

  private ensureGenerator(s: Source) {
    const key = `${s.family}:${s.seed}`
    if (this.genNode && this.genKey === key) return
    this.dropGenerator()
    this.genKey = key
    this.genNode = new AudioWorkletNode(this.eng.ctx, 'megaspace-generator', {
      numberOfInputs: 0,
      numberOfOutputs: 1,
      outputChannelCount: [1],
      processorOptions: { family: s.family, seed: s.seed, running: s.playing },
    })
    const p = this.genNode.parameters
    p.get('a')!.value = s.params[0]
    p.get('b')!.value = s.params[1]
    p.get('c')!.value = s.params[2]
    this.genNode.connect(this.clipIn)
    // Generators bypass the clip filters at unity (defaults are transparent).
  }

  private dropGenerator() {
    if (!this.genNode) return
    this.genNode.disconnect()
    this.genNode.port.close()
    this.genNode = null
    this.genKey = ''
  }

  private updateClip(s: Source) {
    const clip = s.clip
    const e = this.eng
    if (!clip) {
      this.stopClip()
      return
    }
    e.set(this.clipFilterHp.frequency, clip.highpass, GAIN_TAU)
    e.set(this.clipFilterLp.frequency, clip.lowpass, GAIN_TAU)
    const rate = 2 ** (clip.pitch / 12)
    for (const p of this.clipPlayers) e.set(p.node.playbackRate, rate, 0.03)

    const mode = clip.retrigger.enabled ? 'retrigger' : clip.loop ? 'loop' : 'once'
    const key = `${clip.assetId}:${mode}`
    const wantActive = s.playing
    if (key !== this.clipKey || wantActive !== this.active) {
      this.stopClip()
      this.clipKey = key
      this.active = wantActive
      if (wantActive) this.startClip(s)
    } else if (mode === 'loop' && !this.clipPlayers.size && wantActive) {
      this.startClip(s)
    }
  }

  private startClip(s: Source) {
    const clip = s.clip!
    const data = this.eng.clipData(clip.assetId)
    if (!data) return // not decoded (yet); engine calls update again once it is
    if (clip.retrigger.enabled) {
      this.rng = new Rng(s.seed)
      const fire = () => {
        if (this.disposed || !this.active) return
        this.playOnce(data, this.lastSource.clip!)
        const mean = this.lastSource.clip!.retrigger.interval
        const irr = this.lastSource.clip!.retrigger.irregularity
        const expo = -Math.log(1 - this.rng.next() * 0.999)
        const next = Math.max(0.1, mean * (1 + irr * (expo - 1)))
        this.retriggerTimer = setTimeout(fire, next * 1000)
      }
      fire()
    } else if (clip.loop) {
      const buf = this.eng.loopBuffer(clip.assetId)!
      const node = new AudioBufferSourceNode(this.eng.ctx, { buffer: buf, loop: true, playbackRate: 2 ** (clip.pitch / 12) })
      node.connect(this.clipIn)
      node.start()
      this.clipPlayers.add({ node })
    } else {
      this.playOnce(data, clip)
    }
  }

  private playOnce(data: AudioBuffer, clip: NonNullable<Source['clip']>) {
    const node = new AudioBufferSourceNode(this.eng.ctx, {
      buffer: data,
      playbackRate: 2 ** (clip.pitch / 12) * (1 + this.rng.bipolar() * 0.02 * clip.retrigger.irregularity),
    })
    node.connect(this.clipIn)
    const play: ClipPlayback = { node }
    node.onended = () => {
      this.clipPlayers.delete(play)
      node.disconnect()
    }
    node.start()
    this.clipPlayers.add(play)
  }

  private stopClip() {
    if (this.retriggerTimer) clearTimeout(this.retriggerTimer)
    this.retriggerTimer = null
    for (const p of this.clipPlayers) {
      try {
        p.node.stop()
      } catch {
        /* already stopped */
      }
      p.node.disconnect()
    }
    this.clipPlayers.clear()
    this.clipKey = ''
    this.active = false
  }

  /** A clip became available after the voice was created. */
  refresh() {
    this.clipKey = ''
    this.update(this.lastSource, this.eng.anySolo)
  }

  click(buffer: AudioBuffer, when = 0) {
    const n = new AudioBufferSourceNode(this.eng.ctx, { buffer })
    n.connect(this.clickIn)
    n.start(when)
    n.onended = () => n.disconnect()
  }

  /**
   * Early reflections come and go as the listener moves. A surface keeps the same chain while it keeps reflecting
   * (so its delay and position glide smoothly); a chain that stops reflecting fades out and is only reused after
   * it is silent, then jumps straight to its new path.
   */
  private applyEarly(paths: PathInfo[]) {
    const now = this.eng.ctx.currentTime
    const wanted = new Map(paths.map((p) => [p.id, p]))
    for (let i = 0; i < this.early.length; i++) {
      const id = this.slotIds[i]
      if (id === null || id === undefined) continue
      const p = wanted.get(id)
      if (p) {
        this.early[i]!.apply(p)
        wanted.delete(id)
      } else {
        this.early[i]!.apply({ valid: false, gain: 0, delay: 0, cutoff: 20000, apparent: [0, 0, 0] })
        this.slotIds[i] = null
        this.slotFreeAt[i] = this.eng.immediate ? now : now + 0.15
      }
    }
    for (const p of wanted.values()) {
      const j = this.slotIds.findIndex((id, k) => id === null && this.slotFreeAt[k]! <= now)
      if (j < 0) continue // every chain busy fading out: this surface joins on the next update
      this.early[j]!.reset()
      this.early[j]!.apply(p)
      this.slotIds[j] = p.id
    }
  }

  applyAcoustics(r: SourceAcoustics) {
    this.direct.apply(r.direct)
    this.applyEarly(r.early)
    this.send.apply({ valid: true, gain: r.tail.gain, delay: r.tail.delay, cutoff: r.tail.cutoff, apparent: [0, 0, 0] })
  }

  dispose() {
    this.disposed = true
    if (this.stopTimer) clearTimeout(this.stopTimer)
    this.stopClip()
    this.dropGenerator()
    for (const n of [this.level, this.hfCut, this.tap, this.clickIn, this.clipIn, this.clipFilterHp, this.clipFilterLp]) n.disconnect()
    this.direct.dispose()
    for (const c of this.early) c.dispose()
    this.send.dispose()
  }
}

export class AudioEngine {
  readonly layers: Record<Layer, GainNode>
  readonly reverbBus: GainNode
  readonly immediate: boolean
  private master: GainNode
  private limiter: DynamicsCompressorNode
  private analyser: AnalyserNode
  private reverb: AudioWorkletNode
  private voices = new Map<string, Voice>()
  private clips = new Map<string, Clip>()
  private clipBuffers = new Map<string, AudioBuffer>()
  private loopBuffers = new Map<string, AudioBuffer>()
  private clickBuffer: AudioBuffer
  private lastRoom = ''
  private lastAcoustics: AcousticsResult | null = null
  private soloLayer: Layer | null = null
  private sources: Source[] = []
  private levelBuf: Float32Array<ArrayBuffer>

  private constructor(
    readonly ctx: BaseAudioContext,
    opts: EngineOptions,
  ) {
    this.immediate = !!opts.immediate
    this.master = new GainNode(ctx, { gain: 0.8 })
    this.limiter = new DynamicsCompressorNode(ctx, { threshold: -8, knee: 6, ratio: 14, attack: 0.003, release: 0.15 })
    this.analyser = new AnalyserNode(ctx, { fftSize: 1024 })
    this.levelBuf = new Float32Array(1024)
    this.master.connect(this.limiter)
    this.limiter.connect(this.analyser)
    this.limiter.connect(ctx.destination)
    this.layers = {
      direct: new GainNode(ctx),
      early: new GainNode(ctx),
      late: new GainNode(ctx),
    }
    for (const l of Object.values(this.layers)) l.connect(this.master)
    this.reverbBus = new GainNode(ctx, { gain: 1, channelCount: 1, channelCountMode: 'explicit' })
    this.reverb = new AudioWorkletNode(ctx, 'megaspace-reverb', {
      numberOfInputs: 1,
      numberOfOutputs: 1,
      outputChannelCount: [2],
      channelCount: 1,
      channelCountMode: 'explicit',
      processorOptions: { room: opts.room },
    })
    this.reverbBus.connect(this.reverb)
    this.reverb.connect(this.layers.late)
    this.clickBuffer = makeClickBuffer(ctx)
  }

  static async create(ctx: BaseAudioContext, opts: EngineOptions = {}): Promise<AudioEngine> {
    await ctx.audioWorklet.addModule(workletUrl)
    return new AudioEngine(ctx, opts)
  }

  get anySolo() {
    return this.sources.some((s) => s.solo)
  }

  /** Smoothly (or instantly, offline) move a parameter to a value. */
  set(param: Param, value: number, tau: number) {
    if (!Number.isFinite(value)) return
    if (this.immediate) param.value = value
    else param.setTargetAtTime(value, this.ctx.currentTime, tau)
  }

  setPannerPos(p: PannerNode, pos: [number, number, number]) {
    if (p.positionX) {
      this.set(p.positionX, pos[0], MOVE_TAU)
      this.set(p.positionY, pos[1], MOVE_TAU)
      this.set(p.positionZ, pos[2], MOVE_TAU)
    } else {
      ;(p as unknown as { setPosition(x: number, y: number, z: number): void }).setPosition(pos[0], pos[1], pos[2])
    }
  }

  async resume() {
    if (this.ctx instanceof AudioContext && this.ctx.state !== 'running') await this.ctx.resume()
  }

  // ---- Clips ---------------------------------------------------------------------------------------

  /** Decode an imported file, downmix to mono, and remember it under `assetId`. Returns its duration (s). */
  async loadClip(assetId: string, bytes: ArrayBuffer): Promise<number> {
    const decoded = await this.ctx.decodeAudioData(bytes.slice(0))
    const channels = Array.from({ length: decoded.numberOfChannels }, (_, i) => decoded.getChannelData(i))
    this.setClipData(assetId, downmixToMono(channels), decoded.sampleRate)
    return decoded.duration
  }

  setClipData(assetId: string, mono: Float32Array, sampleRate: number) {
    this.clips.set(assetId, { mono, sampleRate })
    this.clipBuffers.delete(assetId)
    this.loopBuffers.delete(assetId)
    for (const v of this.voices.values()) v.refresh()
  }

  hasClip(assetId: string) {
    return this.clips.has(assetId)
  }

  clipData(assetId: string): AudioBuffer | null {
    const clip = this.clips.get(assetId)
    if (!clip) return null
    let buf = this.clipBuffers.get(assetId)
    if (!buf) {
      buf = this.ctx.createBuffer(1, clip.mono.length, clip.sampleRate)
      buf.copyToChannel(clip.mono as Float32Array<ArrayBuffer>, 0)
      this.clipBuffers.set(assetId, buf)
    }
    return buf
  }

  loopBuffer(assetId: string): AudioBuffer | null {
    const clip = this.clips.get(assetId)
    if (!clip) return null
    let buf = this.loopBuffers.get(assetId)
    if (!buf) {
      const data = makeSeamlessLoop(clip.mono, clip.sampleRate)
      buf = this.ctx.createBuffer(1, data.length, clip.sampleRate)
      buf.copyToChannel(data as Float32Array<ArrayBuffer>, 0)
      this.loopBuffers.set(assetId, buf)
    }
    return buf
  }

  // ---- Scene sync ----------------------------------------------------------------------------------

  syncSources(sources: Source[]) {
    this.sources = sources
    const ids = new Set(sources.map((s) => s.id))
    for (const [id, v] of this.voices) {
      if (!ids.has(id)) {
        v.dispose()
        this.voices.delete(id)
      }
    }
    const anySolo = sources.some((s) => s.solo)
    for (const s of sources) {
      let v = this.voices.get(s.id)
      if (!v) {
        v = new Voice(this, s)
        this.voices.set(s.id, v)
        const r = this.lastAcoustics?.sources.find((x) => x.id === s.id)
        if (r) v.applyAcoustics(r)
      }
      v.update(s, anySolo)
    }
  }

  setListener(l: Pick<Listener, 'position' | 'yaw' | 'pitch'>) {
    const L = this.ctx.listener
    const cy = Math.cos(l.yaw),
      sy = Math.sin(l.yaw)
    const cp = Math.cos(l.pitch),
      sp = Math.sin(l.pitch)
    // three.js 'YXZ' camera convention: forward starts at -Z.
    const fwd = [-sy * cp, sp, -cy * cp]
    const up = [sy * sp, cp, cy * sp]
    if (L.positionX) {
      this.set(L.positionX, l.position[0], LISTENER_TAU)
      this.set(L.positionY, l.position[1], LISTENER_TAU)
      this.set(L.positionZ, l.position[2], LISTENER_TAU)
      this.set(L.forwardX, fwd[0]!, LISTENER_TAU)
      this.set(L.forwardY, fwd[1]!, LISTENER_TAU)
      this.set(L.forwardZ, fwd[2]!, LISTENER_TAU)
      this.set(L.upX, up[0]!, LISTENER_TAU)
      this.set(L.upY, up[1]!, LISTENER_TAU)
      this.set(L.upZ, up[2]!, LISTENER_TAU)
    } else {
      const old = L as unknown as {
        setPosition(x: number, y: number, z: number): void
        setOrientation(fx: number, fy: number, fz: number, ux: number, uy: number, uz: number): void
      }
      old.setPosition(...l.position)
      old.setOrientation(fwd[0]!, fwd[1]!, fwd[2]!, up[0]!, up[1]!, up[2]!)
    }
  }

  applyAcoustics(result: AcousticsResult) {
    this.lastAcoustics = result
    this.applyRoom(result.room)
    for (const s of result.sources) this.voices.get(s.id)?.applyAcoustics(s)
  }

  private applyRoom(room: RoomAcoustics) {
    const key = [room.rt60Low.toFixed(2), room.rt60High.toFixed(2), ...room.delays.map((d) => d.toFixed(4))].join(',')
    if (key === this.lastRoom) return
    this.lastRoom = key
    this.reverb.port.postMessage({ type: 'room', room: { delays: room.delays, rt60Low: room.rt60Low, rt60High: room.rt60High } })
  }

  // ---- Controls ------------------------------------------------------------------------------------

  setMasterGain(g: number) {
    this.set(this.master.gain, g, 0.03)
  }

  /** Solo one acoustic layer (direct / early / late); null restores all three. */
  setLayerSolo(layer: Layer | null) {
    this.soloLayer = layer
    for (const [name, node] of Object.entries(this.layers) as [Layer, GainNode][]) {
      this.set(node.gain, layer === null || layer === name ? 1 : 0, 0.03)
    }
  }

  get layerSolo() {
    return this.soloLayer
  }

  /** Fire a short click through a source's full acoustic path (works even if the source is muted). */
  clickTest(sourceId: string, when = 0): boolean {
    const v = this.voices.get(sourceId)
    if (!v) return false
    v.click(this.clickBuffer, when)
    return true
  }

  /** RMS level of the master output (0..1), for meters and tests. */
  level(): number {
    this.analyser.getFloatTimeDomainData(this.levelBuf)
    let s = 0
    for (let i = 0; i < this.levelBuf.length; i++) s += this.levelBuf[i]! * this.levelBuf[i]!
    return Math.sqrt(s / this.levelBuf.length)
  }

  get voiceCount() {
    return this.voices.size
  }

  async dispose() {
    for (const v of this.voices.values()) v.dispose()
    this.voices.clear()
    this.reverb.disconnect()
    this.master.disconnect()
    if (this.ctx instanceof AudioContext && this.ctx.state !== 'closed') await this.ctx.close()
  }
}

/** ~1.2 ms Hann-windowed 3 kHz burst: sharp enough to read echo delays, soft enough not to hurt. */
function makeClickBuffer(ctx: BaseAudioContext): AudioBuffer {
  const sr = ctx.sampleRate
  const n = Math.round(sr * 0.0012)
  const buf = ctx.createBuffer(1, n + 1, sr)
  const d = buf.getChannelData(0)
  for (let i = 0; i < n; i++) {
    const w = 0.5 - 0.5 * Math.cos((2 * Math.PI * (i + 0.5)) / n)
    d[i] = w * Math.sin((2 * Math.PI * 3000 * i) / sr) * 1.6
  }
  return buf
}
